import './paginator.scss';
import classNames from 'classnames';
import { useEffect } from 'react';
import { Button } from '../form/button';
import { PaginationData, PaginationRequest } from '../../clients/quipli-service/pagination/pagination';
import { Typography } from '../typography/typography';
import { SelectForm } from '../select-form/select-form';
import useLocalStorage from 'use-local-storage';

export type PaginatorProps<T extends PaginationRequest> = {
  request?: T;
  setRequest?: (req: PaginationRequest) => void;
  pagination?: PaginationData;
  cypressPrevious?: string;
  cypressNext?: string;
  className?: string;
};

export const Paginator = <T extends PaginationRequest>({
  pagination,
  setRequest,
  request,
  className,
}: PaginatorProps<T>) => {
  const [pageSizePreference, setPageSizePreference] = useLocalStorage<number>('DefaultPageSize', 10);

  const pageSizeOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
  ];

  const setPage = (newPageNumber: number) => {
    if (request?.pageNumber !== newPageNumber) {
      setRequest?.({ pageNumber: newPageNumber });
    }
  };

  const setRowSize = (newPageSize: number) => {
    if (request?.pageSize !== newPageSize) {
      setRequest?.({ pageSize: newPageSize });
      setPageSizePreference(newPageSize);

      if (pagination) {
        const newCalculatedLastPage: number = Math.ceil(pagination.total / newPageSize);
        if (request?.pageNumber && request.pageNumber > newCalculatedLastPage) {
          setRequest?.({ pageNumber: newCalculatedLastPage });
        }
      }
    }
  };

  useEffect(() => {
    if (!request?.pageSize) {
      setRequest?.({ pageSize: pageSizePreference });
    }
  }, [request?.pageSize, pageSizePreference]);

  useEffect(() => {
    if (pagination?.lastPage && request?.pageNumber && request.pageNumber > pagination.lastPage) {
      setRequest?.({ pageNumber: pagination.lastPage });
    }
  }, [pagination?.lastPage]);

  return (
    <div className={classNames('paginator rounded-b-[12px] bg-white', className)}>
      <div className="md:min-w-[100px]">
        <Button
          className="button-secondary"
          disabled={!pagination?.prevPage}
          onClick={() => setPage(pagination?.prevPage || 1)}
          data-cy="previous_page"
        >
          <i className="fa-solid fa-chevron-left hidden md:inline"></i>
          <i className="fa-solid fa-arrow-left inline md:hidden"></i>
          <span className="hidden md:inline">Previous</span>
        </Button>
      </div>
      <div className="md:min-w-[50px]">
        <SelectForm
          isSearchable={false}
          options={pageSizeOptions}
          menuPlacement="top"
          placeholder="Size"
          onChange={(c) => {
            c?.value && setRowSize(c.value);
          }}
          value={pageSizeOptions.filter((option) => option.value === request?.pageSize)}
        />
      </div>

      {pagination && (
        <>
          {/* Tablet/desktop view */}
          <div className="visible-pages hidden md:flex">
            <Button
              className={classNames('button-tertiary-paginator', {
                active: 1 === pagination.pageNumber,
              })}
              onClick={() => setPage(1)}
            >
              1
            </Button>
            {pagination.pageNumber > 3 && <div className="ellipsis">...</div>}
            {Array.from(Array(5).keys())
              .map((offset) => offset - 2)
              .filter(
                (offset) => pagination.pageNumber + offset > 1 && pagination.pageNumber + offset < pagination.lastPage,
              )
              .map((offset) => (
                <Button
                  key={`page_${offset}`}
                  className={classNames('button-tertiary-paginator', {
                    active: offset === 0,
                  })}
                  onClick={() => setPage(pagination.pageNumber + offset)}
                >
                  {pagination.pageNumber + offset}
                </Button>
              ))}
            {pagination.pageNumber < pagination.lastPage - 3 && <div className="ellipsis">...</div>}
            {pagination.lastPage > 1 && (
              <Button
                className={classNames('button-tertiary-paginator', {
                  active: pagination.lastPage === pagination.pageNumber,
                })}
                onClick={() => setPage(pagination.lastPage)}
              >
                {pagination.lastPage}
              </Button>
            )}
          </div>

          {/* Mobile view */}
          <div className="inline md:hidden">
            <Typography type="text-sm" className="text-grey-700">
              Page {pagination.pageNumber} of {pagination.lastPage}
            </Typography>
          </div>
        </>
      )}

      <div className="md:min-w-[100px]">
        <Button
          className="button-secondary float-right"
          disabled={!pagination?.nextPage}
          onClick={() => setPage(pagination?.nextPage || 1)}
          data-cy="next_page"
        >
          <span className="hidden md:inline">Next</span>
          <i className="fa-solid fa-chevron-right hidden md:inline"></i>
          <i className="fa-solid fa-arrow-right inline md:hidden"></i>
        </Button>
      </div>
    </div>
  );
};
